// TODO: Review AI Code for feature: System Landscape & Interactions Visualization - Entire file is AI-generated
import * as THREE from 'three';
import type { SystemLogoData } from '@bliks-io/contracts';

// ============================================================================
// Shared utilities
// ============================================================================

/** Get the display URL from a SystemLogoData object. */
export function getLogoUrl(logo?: SystemLogoData): string | undefined {
  if (!logo) return undefined;
  if (logo.type === 'svg') return logo.svg;
  return logo.thumbnail?.webp || logo.thumbnail?.png;
}

// ============================================================================
// Shared color palette
// ============================================================================

/** Core graph colors used across all visualization components (2D, 3D, Flow). */
export const GRAPH_COLORS = {
  green: '#10B981',
  blue: '#3B82F6',
  purple: '#8B5CF6'
} as const;

/** Convert a hex color to rgba with the given alpha. */
export function withAlpha(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// ============================================================================
// Shared logo cache (single instance across all 3D components)
// ============================================================================

export const logoCache = new Map<string, HTMLImageElement>();

/**
 * Preload logo images for graph nodes. Calls onLoaded once all logos are cached.
 * Returns a cleanup function that prevents the stale callback from firing.
 */
export function preloadLogos(nodes: { logoUrl?: string }[], onLoaded: () => void): () => void {
  let cancelled = false;
  let pending = 0;

  const checkDone = () => {
    if (pending === 0 && !cancelled) onLoaded();
  };

  for (const node of nodes) {
    if (node.logoUrl && !logoCache.has(node.logoUrl)) {
      pending++;
      const img = new Image();
      img.crossOrigin = 'anonymous';
      const url = node.logoUrl;
      img.onload = () => {
        if (!cancelled) {
          logoCache.set(url, img);
        }
        pending--;
        checkDone();
      };
      img.onerror = () => {
        pending--;
        checkDone();
      };
      img.src = url;
    }
  }
  checkDone();

  return () => {
    cancelled = true;
  };
}

// ============================================================================
// Canvas drawing helpers
// ============================================================================

/** Draw a rounded rectangle path (does not fill or stroke — caller decides) */
function roundRect(
  ctx: CanvasRenderingContext2D,
  { x, y, w, h, r }: { x: number; y: number; w: number; h: number; r: number }
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

/** Draw a small external-link icon (box with arrow) on canvas */
function drawExternalLinkIcon(
  ctx: CanvasRenderingContext2D,
  { x, y, size, color }: { x: number; y: number; size: number; color: string }
) {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  const s = size;
  const inset = s * 0.3;
  ctx.beginPath();
  ctx.moveTo(x + inset, y);
  ctx.lineTo(x, y);
  ctx.lineTo(x, y + s);
  ctx.lineTo(x + s, y + s);
  ctx.lineTo(x + s, y + inset);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(x + s * 0.4, y + s * 0.6);
  ctx.lineTo(x + s, y);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(x + s * 0.55, y);
  ctx.lineTo(x + s, y);
  ctx.lineTo(x + s, y + s * 0.45);
  ctx.stroke();
  ctx.restore();
}

/** Draw image preserving aspect ratio (object-fit: contain) within a square box */
function drawLogoContain(
  ctx: CanvasRenderingContext2D,
  { img, x, y, boxSize }: { img: HTMLImageElement; x: number; y: number; boxSize: number }
) {
  const aspect = img.naturalWidth / img.naturalHeight;
  let drawW = boxSize;
  let drawH = boxSize;
  if (aspect > 1) {
    drawH = boxSize / aspect;
  } else {
    drawW = boxSize * aspect;
  }
  ctx.drawImage(img, x + (boxSize - drawW) / 2, y + (boxSize - drawH) / 2, drawW, drawH);
}

// ============================================================================
// Sprite helpers
// ============================================================================

/** Distance (in world units) to push the sprite toward the camera */
const CAMERA_OFFSET = 8;

/**
 * Wrap a sprite in a Group and offset it toward the camera each frame
 * so links/arrows don't poke through the card (billboard z-fighting fix).
 */
function wrapSpriteWithCameraOffset(sprite: THREE.Sprite): THREE.Group {
  const group = new THREE.Group();
  group.add(sprite);
  const _p = new THREE.Vector3();
  const _d = new THREE.Vector3();
  sprite.onBeforeRender = (_r: THREE.WebGLRenderer, _s: THREE.Scene, camera: THREE.Camera) => {
    if (sprite.parent) {
      sprite.parent.getWorldPosition(_p);
      _d.subVectors(camera.position, _p).normalize().multiplyScalar(CAMERA_OFFSET);
      sprite.position.copy(_d);
    }
  };
  return group;
}

// ============================================================================
// HTML helpers
// ============================================================================

/** Escape user-supplied strings before interpolating into HTML template literals. */
function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Build consistent tooltip HTML for 3D graph components.
 * All interpolated values are HTML-escaped to prevent XSS.
 */
export function buildTooltipHtml(
  title: string,
  sections: { label: string; items: { name: string; detail: string }[] }[]
): string {
  const sectionHtml = sections
    .filter((s) => s.items.length > 0)
    .map((section) => {
      const rows = section.items
        .map(
          (item) =>
            `<div class="flex justify-between gap-3 text-xs"><span class="truncate">${escapeHtml(item.name)}</span><span class="text-muted-foreground shrink-0">${escapeHtml(item.detail)}</span></div>`
        )
        .join('');
      const labelHtml = section.label
        ? `<div class="text-xs font-semibold text-muted-foreground mb-0.5">${escapeHtml(section.label)}</div>`
        : '';
      return `<div class="mt-1.5 pt-1.5 border-t border-border">${labelHtml}${rows}</div>`;
    })
    .join('');

  return `<div class="overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md max-w-[280px]"><div class="font-semibold">${escapeHtml(title)}</div>${sectionHtml}</div>`;
}

/** CSS reset for react-force-graph-3d tooltip container */
export const TOOLTIP_RESET_CSS = `.float-tooltip-kap { background: transparent !important; border: none !important; padding: 0 !important; color: inherit !important; font: inherit !important; border-radius: 0 !important; }`;

/**
 * Extract the Sprite child from a __threeObj (which may be Group or Sprite).
 * Returns null if not found.
 */
export function extractSprite(obj: THREE.Object3D | undefined): THREE.Sprite | null {
  if (!obj) return null;
  if (obj instanceof THREE.Group) {
    const sprite = obj.children.find((c): c is THREE.Sprite => c instanceof THREE.Sprite);
    return sprite ?? null;
  }
  if (obj instanceof THREE.Sprite) return obj;
  return null;
}

// ============================================================================
// Shared node sprite renderer
// ============================================================================

export interface NodeSpriteConfig {
  name: string;
  logoUrl?: string;
  /** Whether this is the "large" variant (hub node or current system). */
  isLarge: boolean;
  /** Whether to show a dashed border (e.g. isolated nodes). */
  isDashed: boolean;
  /** Whether to dim the text color (e.g. isolated nodes). */
  isDimmed: boolean;
  /** Whether to show the external-link icon on the right. */
  showLinkIcon: boolean;
  /** Override border color when not hovered. Defaults to '#e2e8f0'. */
  borderColor?: string;
  /** Override border width when not hovered. Defaults to 1.5. */
  borderWidth?: number;
  /** Override font size. Defaults to 18 (large) / 15 (small). */
  fontSize?: number;
  /** Override logo size. Defaults to 28 (large) / 22 (small). */
  logoSize?: number;
  /** Override sprite scale. Defaults to 0.16 (large) / 0.13 (small). */
  scale?: number;
}

/** Resolve config-dependent sizing and style values for a node sprite. */
function resolveSpriteLayout(config: NodeSpriteConfig) {
  return {
    fontSize: config.fontSize ?? (config.isLarge ? 18 : 15),
    logoSize: config.logoSize ?? (config.isLarge ? 28 : 22),
    scale: config.scale ?? (config.isLarge ? 0.16 : 0.13),
    fontWeight: config.isLarge ? 'bold ' : '',
    borderColor: config.borderColor ?? '#e2e8f0',
    borderWidth: config.borderWidth ?? 1.5
  };
}

/**
 * Render a graph node as a canvas-based billboard sprite.
 * Shared between SystemLandscape3D and SystemInteractions3D.
 */
export function createNodeSprite(config: NodeSpriteConfig, hover = false): THREE.Group {
  const layout = resolveSpriteLayout(config);
  const iconSize = 12;
  const pad = 14;
  const gap = 8;
  const fontStr = `${layout.fontWeight}${layout.fontSize}px sans-serif`;

  const measure = document.createElement('canvas');
  const mCtx = measure.getContext('2d')!;
  mCtx.font = fontStr;
  const textW = mCtx.measureText(config.name).width;

  const logoImg = config.logoUrl ? logoCache.get(config.logoUrl) : undefined;
  const hasLogo = !!logoImg;

  const contentW =
    (hasLogo ? layout.logoSize + gap : 0) +
    textW +
    (config.showLinkIcon ? gap * 0.6 + iconSize : 0);
  const cardW = Math.ceil(contentW + pad * 2);
  const cardH = Math.ceil(Math.max(layout.logoSize, layout.fontSize + 4) + pad * 2);

  const canvas = document.createElement('canvas');
  canvas.width = cardW * 2;
  canvas.height = cardH * 2;
  const ctx = canvas.getContext('2d')!;
  ctx.scale(2, 2);

  ctx.fillStyle = hover ? '#f8fafc' : '#ffffff';
  ctx.fillRect(0, 0, cardW, cardH);

  let strokeColor = layout.borderColor;
  if (config.isDashed) strokeColor = '#d1d5db';
  else if (hover) strokeColor = GRAPH_COLORS.purple;
  ctx.strokeStyle = strokeColor;
  ctx.lineWidth = hover ? 2.5 : layout.borderWidth;
  if (config.isDashed) ctx.setLineDash([4, 3]);
  roundRect(ctx, { x: 1, y: 1, w: cardW - 2, h: cardH - 2, r: 8 });
  ctx.stroke();
  ctx.setLineDash([]);

  let textX = pad;
  if (hasLogo && logoImg) {
    const logoY = (cardH - layout.logoSize) / 2;
    drawLogoContain(ctx, { img: logoImg, x: pad, y: logoY, boxSize: layout.logoSize });
    textX = pad + layout.logoSize + gap;
  }

  ctx.fillStyle = config.isDimmed ? '#9CA3AF' : '#1a1a1a';
  ctx.font = fontStr;
  ctx.textBaseline = 'middle';
  ctx.fillText(config.name, textX, cardH / 2);

  if (config.showLinkIcon) {
    const iconX = textX + textW + gap * 0.6;
    const iconY = cardH / 2 - iconSize / 2;
    drawExternalLinkIcon(ctx, {
      x: iconX,
      y: iconY,
      size: iconSize,
      color: hover ? GRAPH_COLORS.purple : '#a0a0a0'
    });
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  const material = new THREE.SpriteMaterial({ map: texture, alphaTest: 0.5, depthWrite: true });
  const sprite = new THREE.Sprite(material);
  sprite.renderOrder = 10;

  sprite.scale.set(cardW * layout.scale, cardH * layout.scale, 1);

  return wrapSpriteWithCameraOffset(sprite);
}
